import {
  useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type InputHTMLAttributes,
} from 'react'
import { createPortal, flushSync } from 'react-dom'

/** A PHONE-NUMBER BOX WITH A TELEPHONE KEYPAD UNDER IT (23 Oct 2026).
 *
 *  The customer: "whenever we need to enter a phone number, it should come up
 *  a small pop-up right under the line ... as a phone keypad ... when we're
 *  gonna use the dispatch panel on a touch screen". His Q1 A: it opens by
 *  itself only when the box is TAPPED with a finger or a pen, never a mouse,
 *  so a desk with a keyboard never has it over the form; the dial-pad button
 *  in the box opens or hides it for anybody. Q2 A: every box that takes a
 *  number.
 *
 *  IT TYPES INTO THE BOX ITSELF: the prototype's value setter (which React's
 *  own value tracker does not see, so the `input` event that follows reads as
 *  a change) at the cursor. Every screen's `onChange` -- the caller-ID look-up,
 *  the past calls, the draft -- runs exactly as for a keyboard.
 *
 *  WHAT A REVIEW FOUND, each easy to put back:
 *  - THE PAD FLOATS ABOVE THE PAGE (a portal on `document.body`, fixed to the
 *    box's place on screen, above it when there is no room below). Inside the
 *    page it was cut off by the open call's `.box { overflow: hidden }` -- a
 *    number could not be added there at all on a touch-only console.
 *  - IT OPENS ON THE TAP, NOT THE PRESS. Opened on `pointerdown` a swipe that
 *    started on the box opened it, and moving the page under a finger still
 *    down put a stray digit in. The press only says "no on-screen keyboard"
 *    (so the tablet's own does not flash up); the `click` opens it.
 *  - HOLD 0 IS ONE PRESS: a second finger is ignored, the browser's long-press
 *    menu is cancelled, and closing clears the timer -- or a + arrived later
 *    in a closed box.
 *  - A KEY'S PRESS IS NOT LEAVING THE BOX (`pressing`): on an iPad focus can
 *    leave with no `relatedTarget`, which closed the pad before the key's
 *    click landed. And a tap outside closes it even where it does not blur.
 *
 *  Telephone layout, 1 2 3 on top and 0 at the bottom, no * and no # (his
 *  words); ⌫ and Done in the corners; holding 0 types + for a number from
 *  another country, as a phone does. */
export function PhoneInput({
  wrapClass, onKeyDown, onPointerDown, onClick, onBlur, inputMode, ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  /** A class for the wrapper, which takes the input's place in its row. */
  wrapClass?: string
}) {
  const box = useRef<HTMLInputElement>(null)
  const wrap = useRef<HTMLSpanElement>(null)
  const padRef = useRef<HTMLDivElement>(null)
  const padId = useId()
  const [open, setOpen] = useState(false)
  /** A finger is on the box: ask for no on-screen keyboard before it focuses. */
  const [noKeyboard, setNoKeyboard] = useState(false)
  const touched = useRef(false)
  /** A key is being pressed: focus moving now is not leaving the box. */
  const pressing = useRef(false)
  /** Holding 0: the timer that types +, and whether it already did. */
  const zero = useRef<{ timer: number | null; plus: boolean }>({ timer: null, plus: false })
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)

  const clearZero = () => {
    if (zero.current.timer !== null) window.clearTimeout(zero.current.timer)
    zero.current.timer = null
  }
  const close = useCallback(() => {
    clearZero()
    setOpen(false)
    setNoKeyboard(false)
    setPos(null)
  }, [])
  useEffect(() => clearZero, [])

  /** Where the pad goes: under the box, or above it with no room below. */
  const place = useCallback(() => {
    const el = box.current
    if (!el) return
    const r = el.getBoundingClientRect()
    if (r.bottom < 0 || r.top > window.innerHeight) { close(); return }
    const h = padRef.current?.offsetHeight ?? 262
    const w = padRef.current?.offsetWidth ?? 252
    const below = r.bottom + 6
    const top = below + h > window.innerHeight - 8 && r.top - 6 - h > 8 ? r.top - 6 - h : below
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8))
    setPos({ left, top })
  }, [close])
  useLayoutEffect(() => {
    if (!open) return
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  // A TAP ANYWHERE ELSE CLOSES IT, including where that does not blur the box.
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => {
      const at = e.target as Node
      if (wrap.current?.contains(at) || padRef.current?.contains(at)) return
      close()
    }
    document.addEventListener('pointerdown', away, true)
    return () => document.removeEventListener('pointerdown', away, true)
  }, [open, close])

  /** One key: at the cursor, as a keyboard would type it. */
  function type(key: string) {
    const el = box.current
    if (!el) return
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    let next: string
    let caret: number
    if (key === 'del') {
      if (start !== end) { next = el.value.slice(0, start) + el.value.slice(end); caret = start }
      else if (start > 0) { next = el.value.slice(0, start - 1) + el.value.slice(start); caret = start - 1 }
      else return
    } else {
      next = el.value.slice(0, start) + key + el.value.slice(end)
      caret = start + key.length
      // Only what ADDS is held to the box's limit; deleting always works.
      if (rest.maxLength && rest.maxLength > 0 && next.length > rest.maxLength) return
    }
    const set = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
    set?.call(el, next)
    el.dispatchEvent(new Event('input', { bubbles: true }))
    el.focus()
    // The cursor goes back where the key put it, now and after the redraw.
    el.setSelectionRange(caret, caret)
    requestAnimationFrame(() => el.setSelectionRange(caret, caret))
  }

  const keep = (e: { preventDefault: () => void }) => e.preventDefault()
  const KEYS: [string, string][] = [
    ['1', ''], ['2', 'ABC'], ['3', 'DEF'], ['4', 'GHI'], ['5', 'JKL'],
    ['6', 'MNO'], ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
  ]

  const pad = open && createPortal(
    <div className="pk-pad" id={padId} role="group" aria-label="Phone keypad" ref={padRef}
         style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden' }}
         onPointerDown={(e) => { keep(e); pressing.current = true }}
         onPointerUp={() => { window.setTimeout(() => { pressing.current = false }, 0) }}
         onPointerCancel={() => { pressing.current = false; clearZero() }}
         onMouseDown={keep}
         onContextMenu={keep}>
      {KEYS.map(([digit, letters]) => (
        <button key={digit} type="button" tabIndex={-1} aria-label={digit} onClick={() => type(digit)}>
          {digit}<small aria-hidden="true">{letters || ' '}</small>
        </button>
      ))}
      <button type="button" tabIndex={-1} className="pk-del" aria-label="Delete"
              onClick={() => type('del')}>
        ⌫
      </button>
      <button
        type="button" tabIndex={-1} aria-label="0, hold for plus"
        onPointerDown={(e) => {
          // ONE PRESS AT A TIME: a second finger or a palm is not a second hold.
          if (!e.isPrimary || zero.current.timer !== null) return
          zero.current.plus = false
          zero.current.timer = window.setTimeout(() => {
            zero.current.timer = null
            zero.current.plus = true
            type('+')
          }, 600)
        }}
        onPointerLeave={clearZero}
        onPointerCancel={clearZero}
        onClick={() => {
          clearZero()
          // A HOLD ALREADY TYPED +; the release is not also a 0.
          if (zero.current.plus) { zero.current.plus = false; return }
          type('0')
        }}
      >
        0<small aria-hidden="true">+</small>
      </button>
      <button type="button" tabIndex={-1} className="pk-done" onClick={close}>Done</button>
    </div>,
    document.body,
  )

  return (
    <span className={`pk${wrapClass ? ` ${wrapClass}` : ''}`} ref={wrap}>
      <input
        {...rest}
        ref={box}
        inputMode={open || noKeyboard ? 'none' : (inputMode ?? 'tel')}
        onPointerDown={(e) => {
          touched.current = e.pointerType === 'touch' || e.pointerType === 'pen'
          if (touched.current) setNoKeyboard(true)
          onPointerDown?.(e)
        }}
        onPointerCancel={() => {
          // A SWIPE, NOT A TAP: the page scrolled under the finger.
          touched.current = false
          if (!open) setNoKeyboard(false)
        }}
        onClick={(e) => {
          if (touched.current) setOpen(true)
          touched.current = false
          onClick?.(e)
        }}
        onKeyDown={(e) => {
          // ESCAPE CLOSES THE KEYPAD AND NOTHING ELSE: a row editing a number
          // or a dialog listening for Escape must not also go.
          if (open && e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            close()
            return
          }
          onKeyDown?.(e)
        }}
        onBlur={(e) => {
          const to = e.relatedTarget as Node | null
          if (!pressing.current && !wrap.current?.contains(to) && !padRef.current?.contains(to)) close()
          onBlur?.(e)
        }}
      />
      <button type="button" className="pk-toggle" tabIndex={-1}
              aria-label={open ? 'Hide the phone keypad' : 'Show the phone keypad'}
              aria-expanded={open} aria-controls={open ? padId : undefined}
              title={open ? 'Hide the keypad' : 'Phone keypad'}
              onPointerDown={keep} onMouseDown={keep}
              onClick={() => {
                if (open) { close(); box.current?.focus(); return }
                // THE BOX ASKS FOR NO KEYBOARD BEFORE IT IS FOCUSED, or a
                // tablet shows its own and then hides it.
                flushSync(() => { setNoKeyboard(true); setOpen(true) })
                box.current?.focus()
              }}>
        {/* A DRAWN DIAL PAD, not the ⌨ character: Windows can render that as
            a coloured emoji, the paperclip's lesson. */}
        <svg viewBox="0 0 14 14" aria-hidden="true">
          {[2, 7, 12].flatMap((y) => [2, 7, 12].map((x) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" fill="currentColor" />
          )))}
        </svg>
      </button>
      {pad}
    </span>
  )
}
